<template>
  <div class="time-container" :style="containerStyle">
    <div v-if="clockType === 'digital'">
      <div class="digital-clock">
        <span :style="digitalClockStyle">{{ formattedTime }}</span>
        <span v-if="!is24Format" class="am-pm" :style="textSizeStyle">
          {{ amPm }}
        </span>
      </div>
    </div>
    <div v-else class="analog-clock" :style="analogClockStyle">
      <div class="clock-face">
        <div class="number number-12">12</div>
        <div class="number number-3">3</div>
        <div class="number number-6">6</div>
        <div class="number number-9">9</div>
        <div v-for="n in 8" :key="n" class="dot" :style="getDotStyle(n)"></div>
        <div class="hand hour-hand" :style="hourHandStyle"></div>
        <div class="hand minute-hand" :style="minuteHandStyle"></div>
        <div class="hand second-hand" :style="secondHandStyle"></div>
        <div
          class="center-dot"
          :style="{
            backgroundColor: app.time?.backgroundColor || '#000000',
            border: `4px solid ${app.time?.clockColor}`,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';

  export default {
    name: 'TimeView',

    props: {
      app: {
        type: Object,
        required: true,
      },
    },

    data() {
      return {
        currentTime: new Date(),
        timer: null,
        containerSize: {
          width: 300,
          height: 300,
        },
        resizeObserver: null,
      };
    },

    mounted() {
      this.updateTime();
      this.timer = setInterval(this.updateTime, 1000);
      this.initResizeObserver();
    },

    beforeDestroy() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },

    methods: {
      updateTime() {
        this.currentTime = new Date();
      },

      initResizeObserver() {
        this.$nextTick(() => {
          const container = this.$el;
          if (container) {
            this.updateContainerSize();
            this.resizeObserver = new ResizeObserver(this.updateContainerSize);
            this.resizeObserver.observe(container);
          }
        });
      },

      updateContainerSize() {
        const container = this.$el;
        if (container) {
          this.containerSize = {
            width: container.clientWidth,
            height: container.clientHeight,
          };
        }
      },

      getDotStyle(number) {
        let position;
        if (number <= 2) {
          position = number;
        } else if (number <= 4) {
          position = number + 1;
        } else if (number <= 6) {
          position = number + 2;
        } else {
          position = number + 3;
        }

        const angleInDegrees = position * 30;
        const angleInRadians = angleInDegrees * (Math.PI / 180);
        const left = `${50 + Math.sin(angleInRadians) * 36}%`;
        const top = `${50 - Math.cos(angleInRadians) * 36}%`;

        const size = Math.min(this.containerSize.width, this.containerSize.height) - 10;

        return {
          left,
          top,
          width: `${Math.min(8, size / 30)}px`,
          height: `${Math.min(8, size / 30)}px`,
          backgroundColor: this.app.time?.clockColor || '#000000',
        };
      },
    },

    computed: {
      timezone() {
        return this.app.time?.timezone || 'UTC';
      },

      is24Format() {
        return this.app.time?.format === '24 hours';
      },

      clockType() {
        return this.app.time?.clockType || 'digital';
      },

      formattedTime() {
        const time = moment(this.currentTime).tz(this.timezone);
        if (this.is24Format) {
          return time.format('HH:mm');
        } else {
          return time.format('h:mm');
        }
      },

      amPm() {
        return moment(this.currentTime).tz(this.timezone).format('A');
      },

      containerStyle() {
        return {
          color: this.app.time?.fontColor || '#000000',
          fontFamily: this.app.time?.textFont || 'Poppins',
          backgroundColor: this.app.time?.backgroundColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        };
      },

      digitalClockStyle() {
        return {
          fontSize: `${this.app.time?.numbersSize || 48}px`,
        };
      },

      textSizeStyle() {
        return {
          fontSize: `${this.app.time?.textSize || 12}px`,
        };
      },

      analogClockStyle() {
        const size = Math.min(this.containerSize.width, this.containerSize.height);
        const fontSize = this.app.time?.numbersSize || 12;

        return {
          width: `${size}px`,
          height: `${size}px`,
          position: 'relative',
          fontSize: `${fontSize}px`,
        };
      },

      hourHandStyle() {
        const time = moment(this.currentTime).tz(this.timezone);
        const hours = time.hours() % 12;
        const minutes = time.minutes();
        const degrees = hours * 30 + minutes * 0.5;
        return {
          transform: `rotate(${degrees}deg)`,
          backgroundColor: this.app.time?.clockColor || '#000000',
        };
      },

      minuteHandStyle() {
        const time = moment(this.currentTime).tz(this.timezone);
        const minutes = time.minutes();
        const degrees = minutes * 6;
        return {
          transform: `rotate(${degrees}deg)`,
          backgroundColor: this.app.time?.clockColor || '#000000',
        };
      },

      secondHandStyle() {
        const time = moment(this.currentTime).tz(this.timezone);
        const seconds = time.seconds();
        const degrees = seconds * 6;
        return {
          transform: `rotate(${degrees}deg)`,
          backgroundColor: this.app.time?.clockColor || '#000000',
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .time-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .digital-clock {
    text-align: center;
    font-weight: bold;
    display: flex;
    align-items: start;
    justify-content: center;

    .am-pm {
      margin-left: 3px;
      margin-top: 3px;
      display: flex;
      align-items: start;
    }
  }

  .analog-clock {
    position: relative;

    .clock-face {
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .number {
      position: absolute;
      font-weight: bold;
    }

    .number-12 {
      top: 8%;
      left: 50%;
      transform: translateX(-50%);
    }

    .number-3 {
      right: 8%;
      top: 50%;
      transform: translateY(-50%);
    }

    .number-6 {
      bottom: 8%;
      left: 50%;
      transform: translateX(-50%);
    }

    .number-9 {
      left: 8%;
      top: 50%;
      transform: translateY(-50%);
    }

    .dot {
      position: absolute;
      border-radius: 50%;
      transform: translate(-50%, -50%);
    }

    .hand {
      position: absolute;
      transform-origin: bottom center;
    }

    .hour-hand {
      width: 6px;
      height: 20%;
      bottom: 50%;
    }

    .minute-hand {
      width: 4px;
      height: 28%;
      bottom: 50%;
    }

    .second-hand {
      width: 2px;
      height: 32%;
      bottom: 50%;
    }

    .center-dot {
      position: absolute;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: currentColor;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
    }
  }
</style>
